import { jsxs, jsx, Fragment as Fragment$1 } from 'react/jsx-runtime';
import { Box, Typography, Tooltip, TextField, useTheme, useMediaQuery, InputAdornment, IconButton, Button, LinearProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { IconContext, AuthContext } from '@plumeuk/shapeshift-identity';
import { useContext, useState, useEffect, Fragment, useRef, useMemo, useCallback } from 'react';
import { useSensors, useSensor, PointerSensor, TouchSensor, KeyboardSensor, DndContext } from '@dnd-kit/core';
import { useSortable, sortableKeyboardCoordinates, arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { isMarkedSingleChoiceAnswer, isMarkedBooleanChoiceAnswer, isMarkedMultipleChoiceAnswer, isMarkedFillTheBlankAnswer, isMarkedDragToOrderAnswer, isMarkedMatchingOrderAnswer, isMarkedTextAnswer, isTextQuestion, isSingleChoiceQuestion, isBooleanChoiceQuestion, isMultipleChoiceQuestion, isFillTheBlankQuestion, isDragToOrderQuestion, isMatchingOrderQuestion, isSingleChoiceAnswer, isMultipleChoiceAnswer, isBooleanChoiceAnswer, isFillTheBlankAnswer, isDragToOrderAnswer, isMatchingOrderAnswer, isTextAnswer } from '@plumeuk/shapeshift-types/IQuiz';
import 'x2js';
import rehypeRaw from 'rehype-raw';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import { isSingleChoiceQuestion as isSingleChoiceQuestion$1, isSingleChoiceAnswer as isSingleChoiceAnswer$1, isMultipleChoiceQuestion as isMultipleChoiceQuestion$1, isMultipleChoiceAnswer as isMultipleChoiceAnswer$1, isBooleanChoiceQuestion as isBooleanChoiceQuestion$1, isBooleanChoiceAnswer as isBooleanChoiceAnswer$1, isFillTheBlankQuestion as isFillTheBlankQuestion$1, isFillTheBlankAnswer as isFillTheBlankAnswer$1, isDragToOrderQuestion as isDragToOrderQuestion$1, isDragToOrderAnswer as isDragToOrderAnswer$1, isMatchingOrderQuestion as isMatchingOrderQuestion$1, isMatchingOrderAnswer as isMatchingOrderAnswer$1 } from '@plumeuk/shapeshift-types';

const useStyles$o = makeStyles()((theme, { isSelected, isError, isCorrect }) => {
    const isMarked = isCorrect !== undefined;
    const getBorderColour = () => {
        if (isMarked)
            return isCorrect ? theme.palette.success.main : theme.palette.error.main;
        if (isSelected)
            return theme.palette.secondary.main;
        return isError ? theme.palette.error.main : null;
    };
    const getBackgroundColor = () => {
        if (isMarked)
            return getBorderColour();
        return null;
    };
    return {
        questionOption: {
            color: isMarked ? "white" : "inherit",
            transition: "all .3s ease-in-out",
            userSelect: "none",
            border: "2px solid " + (getBorderColour() ?? "#d1d1d1"),
            backgroundColor: getBackgroundColor() ?? "inherit",
            padding: "10px",
            borderRadius: "10px",
            display: "flex",
            flexGrow: "2",
            cursor: "pointer",
            "& > div:first-of-type": {
                paddingRight: "20px",
                color: isSelected ? theme.palette.secondary.main : "inherit"
            },
            [theme.breakpoints.down("sm")]: {
                border: "2px solid " + (getBorderColour() ?? "#fff"),
                backgroundColor: getBackgroundColor() ?? "#fff"
            }
        },
        optionId: {
            padding: "12px",
            transition: "all .3s ease-in-out",
            borderRight: "2px solid " + (isMarked ? "#ffffff40" : "#d1d1d1")
        },
        childrenContainer: {
            padding: "12px 20px",
            textAlign: "left",
            flexGrow: 3
        },
        markedIconContainer: {
            justifyContent: "center",
            display: "flex",
            flexDirection: "column",
            "& svg": {
                color: "white",
                fontSize: "35px"
            }
        }
    };
});
const ChoiceQuestionOption = ({ className, i, isSelected, isError, isCorrect, children, ...props }) => {
    const { classes, cx } = useStyles$o({ isSelected, isError, isCorrect });
    const Alpha = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const { checkCircleIcon, cancelIcon } = useContext(IconContext);
    return (jsxs(Box, { className: cx(classes.questionOption, className, isSelected ? "active" : "in-active"), ...props, children: [jsx(Box, { className: classes.optionId, children: Alpha.charAt(i) }), jsx(Box, { className: classes.childrenContainer, children: children }), isCorrect !== undefined &&
                jsx(Box, { className: classes.markedIconContainer, children: isCorrect ? checkCircleIcon : cancelIcon })] }));
};

const useStyles$n = makeStyles()((theme) => ({
    booleanChoiceQuestion: {},
    title: {
        margin: "20px 0 30px 0"
    },
    optionContainer: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
        justifyContent: "space-between",
        columnGap: "30px",
        rowGap: "10px",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0, 1fr)"
        }
    }
}));
const MarkedBooleanChoiceQuestion = ({ className, markedAnswer, ...props }) => {
    const { classes, cx } = useStyles$n();
    return (jsxs(Box, { className: cx(classes.booleanChoiceQuestion, className), ...props, children: [jsx(Typography, { className: classes.title, variant: "h3", children: markedAnswer.title }), jsx(Box, { className: classes.optionContainer, children: [true, false].map((option, i) => (jsx(ChoiceQuestionOption, { "data-test-id": `marked-boolean-choice-question-option-${option ? "true" : "false"}`, i: i, isCorrect: markedAnswer.option === option ? markedAnswer.correct : undefined, children: option ? "True" : "False" }, `quiz-question-option-${i}`))) })] }));
};

const useStyles$m = makeStyles()((theme) => ({
    singleChoiceQuestion: {},
    title: {
        margin: "20px 0 30px 0"
    },
    optionContainer: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
        justifyContent: "space-between",
        columnGap: "30px",
        rowGap: "10px",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0, 1fr)"
        }
    }
}));
const MarkedSingleChoiceQuestion = ({ className, markedAnswer, ...props }) => {
    const { classes, cx } = useStyles$m();
    return (jsxs(Box, { className: cx(classes.singleChoiceQuestion, className), ...props, children: [jsx(Typography, { className: classes.title, variant: "h3", children: markedAnswer.title }), jsx(Box, { className: classes.optionContainer, children: markedAnswer.options.map((option, i) => (jsx(ChoiceQuestionOption, { "data-test-id": `marked-single-choice-question-option-${option.value.replace(" ", "")}`, i: i, isCorrect: option.correct, children: option.value }, `quiz-question-option-${i}`))) })] }));
};

const useStyles$l = makeStyles()((theme) => ({
    multipleChoiceQuestion: {},
    title: {
        margin: "20px 0 30px 0"
    },
    optionContainer: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
        justifyContent: "space-between",
        columnGap: "30px",
        rowGap: "10px",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0, 1fr)"
        }
    }
}));
const MarkedMultipleChoiceQuestion = ({ className, markedAnswer, ...props }) => {
    const { classes, cx } = useStyles$l();
    return (jsxs(Box, { className: cx(classes.multipleChoiceQuestion, className), ...props, children: [jsx(Typography, { className: classes.title, variant: "h3", children: markedAnswer.title }), jsx(Box, { className: classes.optionContainer, children: markedAnswer.options.map((option, i) => {
                    return (jsx(ChoiceQuestionOption, { "data-test-id": `marked-multiple-choice-question-option-${option.value.replace(" ", "")}`, i: i, isCorrect: option.correct, children: option.value }, `quiz-question-option-${i}`));
                }) })] }));
};

const checkWhetherStringIsUrl = (str) => {
    const regex = new RegExp(/((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%\\/.\w-_]*)?\??(?:[-\\+=&;%@.\w_]*)#?(?:[\w]*))?)/);
    return regex.test(str);
};
const replaceAll = (str, find, replace) => {
    // Escape special characters in `find` using a regular expression
    const escapedFind = find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Create a regular expression with global flag to replace all occurrences
    const regex = new RegExp(escapedFind, "g");
    // Use the replace method with the regular expression
    return (str ?? "").replace(regex, replace);
};

const useStyles$k = makeStyles()((theme) => ({
    dragListContainer: {
        width: "100%"
    },
    questionOption: {
        border: "2px solid #d1d1d1",
        padding: "10px",
        borderRadius: "10px",
        userSelect: "none",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexGrow: "2",
        marginTop: "10px",
        "& svg": {
            opacity: 0.2,
            fontSize: "35px"
        },
        [theme.breakpoints.down("sm")]: {
            padding: 0
        }
    },
    childrenClasses: {
        flexGrow: 5,
        padding: "12px 20px",
        [theme.breakpoints.down("sm")]: {
            padding: "10px"
        }
    },
    imageOption: {
        width: "300px"
    },
    imagesOption: {
        objectFit: "contain",
        height: "200px",
        width: "200px"
    },
    successClasses: {
        color: "white",
        background: theme.palette.success.main,
        borderColor: theme.palette.success.main,
        "& svg": {
            opacity: 1
        }
    },
    errorClasses: {
        color: "white",
        background: theme.palette.error.main,
        borderColor: theme.palette.error.main,
        "& svg": {
            opacity: 1
        }
    },
    errorMsg: {
        margin: "10px 0",
        color: "red",
        height: "10px"
    }
}));
const DraggableListItem = ({ item, isDragDisabled, minHeight }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
    const { icon, cancelIcon, checkCircleIcon } = useContext(IconContext);
    const itemStyle = {
        transform: CSS.Translate.toString(transform),
        transition,
        minHeight: (minHeight ?? 0) + "px"
    };
    const { classes, cx } = useStyles$k();
    return (jsxs(Box, { className: cx(classes.questionOption, checkWhetherStringIsUrl(item.value) && classes.imageOption, item.correct === false && classes.errorClasses, item.correct === true && classes.successClasses), ...attributes, ref: setNodeRef, style: itemStyle, children: [!isDragDisabled && icon("dragIndicatorIcon", {
                style: { cursor: "grab", touchAction: "none" },
                ...listeners
            }), jsx(Box, { className: classes.childrenClasses, children: checkWhetherStringIsUrl(item.value) ? (jsx("img", { src: item.value, alt: item.value, className: classes.imagesOption })) : (item.value) }), jsx(Box, { children: item.correct === false && cancelIcon }), jsx(Box, { children: item.correct === true && checkCircleIcon })] }, item.id));
};

const useStyles$j = makeStyles()((theme) => ({
    dragListContainer: {
        width: "100%"
    },
    questionOption: {
        border: "2px solid #d1d1d1",
        padding: "10px",
        borderRadius: "10px",
        userSelect: "none",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexGrow: "2",
        marginTop: "10px",
        cursor: "pointer",
        "& svg": {
            opacity: 0.2,
            fontSize: "35px"
        }
    },
    childrenClasses: {
        flexGrow: 5,
        padding: "12px 20px"
    },
    imageOption: {
        width: "300px"
    },
    imagesOption: {
        objectFit: "contain",
        height: "200px",
        width: "200px"
    },
    successClasses: {
        color: "white",
        background: theme.palette.success.main,
        borderColor: theme.palette.success.main,
        "& svg": {
            opacity: 1
        }
    },
    errorClasses: {
        color: "white",
        background: theme.palette.error.main,
        borderColor: theme.palette.error.main,
        "& svg": {
            opacity: 1
        }
    },
    errorMsg: {
        margin: "10px 0",
        color: "red",
        height: "10px"
    }
}));
const DraggableList = ({ list, onChange, isDragDisabled, minHeight }) => {
    const { classes } = useStyles$j();
    const [draggableList, setDraggableList] = useState([]);
    const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates
    }));
    useEffect(() => {
        setDraggableList(prev => {
            let update = false;
            const newList = [];
            let i = 0;
            for (const e of list) {
                if (prev[i] && e?.id === prev[i].id) {
                    newList.push(prev[i]);
                }
                else {
                    newList.push({ ...e, id: e.id ?? e.value });
                    update = true;
                }
                i++;
            }
            return update ? newList : prev;
        });
    }, [list]);
    useEffect(() => {
        if (draggableList.length > 0)
            onChange?.(draggableList.map(e => e.value));
    }, [draggableList, onChange]);
    return (jsx(Box, { className: classes.dragListContainer, children: jsx(DndContext, { sensors: sensors, onDragEnd: (event) => {
                setDraggableList((items) => {
                    if (event?.active?.id !== event?.over?.id) {
                        const dragIndex = items.findIndex((e) => e.id === event?.active?.id);
                        const hoverIndex = items.findIndex((e) => e.id === event?.over?.id);
                        return arrayMove(items, dragIndex, hoverIndex);
                    }
                    return items;
                });
            }, modifiers: [restrictToVerticalAxis], children: jsx(SortableContext, { items: draggableList, strategy: verticalListSortingStrategy, children: draggableList.map((item) => (jsx(DraggableListItem, { minHeight: minHeight, item: item, isDragDisabled: isDragDisabled }, item.id))) }) }) }));
};

const useStyles$i = makeStyles()(() => ({
    title: {
        margin: "20px 0 30px 0"
    }
}));
const MarkedDragToOrderQuestion = ({ markedAnswer, ...props }) => {
    const { classes } = useStyles$i();
    return (jsxs(Box, { ...props, children: [jsx(Typography, { className: classes.title, variant: "h3", children: markedAnswer.title }), jsx(DraggableList, { title: markedAnswer.title, list: markedAnswer.options, isDragDisabled: true })] }));
};

const useStyles$h = makeStyles()((theme) => ({
    fillTheBlankQuestion: {},
    title: {
        margin: "20px 0 30px 0"
    },
    optionContainer: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
        justifyContent: "space-between",
        columnGap: "30px",
        rowGap: "10px",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0, 1fr)"
        }
    },
    fillTheBlankQuestionText: {
        display: "inline-block",
        verticalAlign: "middle",
        marginBottom: "10px"
    },
    fillTheBlankAnswer: {
        display: "inline-block",
        verticalAlign: "middle",
        marginBottom: "10px",
        color: "white",
        padding: "12px 20px",
        flexGrow: 2,
        borderRadius: "10px"
    },
    fillTheBlankAnswerQuestionText: {
        marginBottom: "5px"
    },
    correctAnswer: {
        background: theme.palette.success.main,
        color: "white"
    },
    incorrectAnswer: {
        background: "#FE4A49",
        color: "white"
    },
    markedIconContainer: {
        marginLeft: "15px",
        display: "inline-block",
        verticalAlign: "middle"
    },
    passIcon: {
        fontSize: "25px"
    },
    correctedAnswer: {
        textDecoration: "line-through",
        marginRight: "10px"
    },
    correctedIcon: {
        padding: "0 0 4px 12px",
        fontSize: "20px"
    }
}));
const MarkedFillTheBlankQuestion = ({ className, markedAnswer, ...props }) => {
    const { classes, cx } = useStyles$h();
    const splitQuestionText = markedAnswer.fillTheBlankQuestion.split("_");
    const { icon } = useContext(IconContext);
    return (jsxs(Box, { className: cx(classes.fillTheBlankQuestion, className), ...props, children: [jsx(Typography, { className: classes.title, variant: "h3", children: "Fill in the blanks result" }), splitQuestionText.map((questionText, i) => {
                const option = markedAnswer.answerOptions[i];
                const optionMarked = markedAnswer.options[i];
                return jsxs(Fragment, { children: [questionText.split(" ").map((e, j) => jsxs(Typography, { className: classes.fillTheBlankQuestionText, "data-test-id": `marked-fill-in-the-blank-question-option-${e.replace(" ", "")}`, children: [e, "\u00A0"] }, "question-text-word" + j)), i < (splitQuestionText.length - 1) &&
                            jsxs(Box, { className: `${optionMarked.correct ? classes.correctAnswer : classes.incorrectAnswer} ${classes.fillTheBlankAnswer}`, children: [jsx(Typography, { className: `${classes.fillTheBlankQuestionText} ${classes.fillTheBlankAnswerQuestionText}`, children: optionMarked.correct ? optionMarked.value : option }), jsxs(Box, { className: classes.markedIconContainer, children: [optionMarked.correct ?
                                                icon("checkCircleIcon", {
                                                    className: classes.passIcon
                                                })
                                                : icon("cancelIcon", {
                                                    className: classes.passIcon
                                                }), optionMarked.correctedSpelling &&
                                                jsx(Tooltip, { title: `This answer was auto corrected from "${optionMarked.correctedSpelling}"`, placement: "right", arrow: true, children: icon("autoFixNormalIcon", {
                                                        className: classes.correctedIcon
                                                    }) })] })] })] }, "question-text-word" + i);
            })] }));
};

const useStyles$g = makeStyles()((theme) => ({
    matchingOrderQuestion: {
        display: "flex",
        justifyContent: "space-between",
        gap: "50px",
        [theme.breakpoints.down("sm")]: {
            gap: "10px"
        }
    },
    title: {
        margin: "20px 0 30px 0"
    },
    errorMsg: {
        margin: "10px 0",
        color: "red",
        height: "10px"
    }
}));
const MarkedMatchingOrderQuestion = ({ markedAnswer, ...props }) => {
    const { classes } = useStyles$g();
    const [firstRender, setFirstRender] = useState(true);
    const container = useRef(null);
    const [minHeight, setMinHeight] = useState(0);
    useEffect(() => setFirstRender(false), []);
    const questionsList = useMemo(() => {
        return markedAnswer.questions.map(option => ({ value: option }));
    }, [markedAnswer]);
    useEffect(() => {
        if (firstRender || !container.current)
            return;
        let biggestHeight = 0;
        Array.from(container.current.children).forEach(e => {
            Array.from(e.children).forEach(x => {
                const height = x.offsetHeight;
                if (height > biggestHeight)
                    biggestHeight = height;
            });
        });
        setMinHeight(biggestHeight);
    }, [firstRender]);
    return (jsxs(Box, { ...props, children: [jsx(Typography, { className: classes.title, variant: "h3", children: markedAnswer.title }), jsxs(Box, { className: classes.matchingOrderQuestion, ref: container, children: [jsx(DraggableList, { list: questionsList, isDragDisabled: true, minHeight: minHeight }), jsx(DraggableList, { list: markedAnswer.options, isDragDisabled: true, minHeight: minHeight })] })] }));
};

const useStyles$f = makeStyles()((theme) => ({
    textQuestion: {},
    title: {
        margin: "20px 0 30px 0"
    },
    inputContainer: {
        position: "relative"
    },
    textField: {
        background: theme.palette.success.main
    },
    textArea: {
        width: "100%",
        minHeight: "100px",
        paddingRight: "35px",
        "& textarea": {
            minHeight: "inherit"
        }
    },
    markedIconContainer: {
        position: "absolute",
        top: "15px",
        right: "15px",
        "& svg": {
            color: "white",
            fontSize: "35px"
        }
    }
}));
const MarkedTextQuestion = ({ className, markedAnswer, ...props }) => {
    const { classes, cx } = useStyles$f();
    const { icon } = useContext(IconContext);
    return (jsxs(Box, { className: cx(classes.textQuestion, className), ...props, children: [jsx(Typography, { className: classes.title, variant: "h3", children: markedAnswer.title }), jsxs(Box, { className: classes.inputContainer, children: [jsx(TextField, { id: "quiz-text-question-input", multiline: true, className: classes.textField, value: markedAnswer?.answer ?? "", placeholder: "...", InputProps: { disableUnderline: true, className: classes.textArea, readOnly: true }, variant: "standard" }), jsx(Box, { className: classes.markedIconContainer, children: icon("checkCircleIcon") })] })] }));
};

const MarkedQuestion = ({ markedAnswer, ...props }) => {
    return (jsxs(Box, { ...props, children: [isMarkedSingleChoiceAnswer(markedAnswer) &&
                jsx(MarkedSingleChoiceQuestion, { markedAnswer: markedAnswer }), isMarkedBooleanChoiceAnswer(markedAnswer) &&
                jsx(MarkedBooleanChoiceQuestion, { markedAnswer: markedAnswer }), isMarkedMultipleChoiceAnswer(markedAnswer) &&
                jsx(MarkedMultipleChoiceQuestion, { markedAnswer: markedAnswer }), isMarkedFillTheBlankAnswer(markedAnswer) &&
                jsx(MarkedFillTheBlankQuestion, { markedAnswer: markedAnswer }), isMarkedDragToOrderAnswer(markedAnswer) &&
                jsx(MarkedDragToOrderQuestion, { markedAnswer: markedAnswer }), isMarkedMatchingOrderAnswer(markedAnswer) &&
                jsx(MarkedMatchingOrderQuestion, { markedAnswer: markedAnswer }), isMarkedTextAnswer(markedAnswer) &&
                jsx(MarkedTextQuestion, { markedAnswer: markedAnswer })] }));
};

const useMq = () => {
    const theme = useTheme();
    const isMobileMq = useMediaQuery(`(max-width:${theme.breakpoints.values.sm - 1}px)`);
    const isTabletMq = useMediaQuery(`(min-width:${theme.breakpoints.values.sm}px) and (max-width:${theme.breakpoints.values.md}px)`);
    const isDesktopMq = useMediaQuery(`(min-width:${theme?.breakpoints.values.md + 1}px)`);
    return {
        mobile: isMobileMq,
        tablet: isTabletMq,
        desktop: isDesktopMq
    };
};

//Hook returns url path to server resources
//With strapi local dev path is relative
function useFileBaseUrl() {
    const { config } = useContext(AuthContext);
    const baseFileUrl = process.env.NODE_ENV === "production" ? "" : (config?.baseApiUrl ?? "");
    const getUrl = (e) => {
        if (e.startsWith("http"))
            return e;
        return baseFileUrl + e;
    };
    return { baseFileUrl, getUrl };
}

/* eslint-disable no-console */
console.log.bind(null) ;
console.warn.bind(null) ;
console.error.bind(null) ;

const useStyles$e = makeStyles()((theme) => ({
    markedQuestionSummary: {},
    container: {
        boxSizing: "border-box",
        borderRadius: "10px",
        padding: "40px 20px",
        width: "100%",
        background: "#BCBCBD33",
        display: "flex",
        justifyContent: "space-around",
        [theme.breakpoints.down("sm")]: {
            padding: "20px 10px",
            background: "#fff"
        }
    },
    scoreLabel: {
        fontWeight: "700",
        [theme.breakpoints.down("sm")]: {
            fontWeight: "500",
            marginBottom: "10px",
            fontSize: "18px"
        }
    },
    label: {
        fontSize: "20px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
    },
    scoreValueContainer: {
        display: "flex",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column"
        }
    },
    scoreValue: {
        fontWeight: "500",
        marginRight: "10px",
        color: theme.palette.secondary.main
    }
}));
const MarkedQuestionsSummary = ({ className, markedAnswers, ...props }) => {
    const { classes, cx } = useStyles$e();
    const mq = useMq();
    const totalQuestions = markedAnswers ? (markedAnswers.correct + markedAnswers.incorrect) : null;
    const accuracy = (totalQuestions && markedAnswers) ? Math.round((markedAnswers.correct / totalQuestions) * 100) : null;
    return (jsxs(Box, { className: cx(classes.markedQuestionSummary, className), ...props, children: [mq.mobile && jsx(Typography, { className: cx(classes.label, classes.scoreLabel), children: "Your Score" }), jsxs(Box, { className: classes.container, children: [!mq.mobile && jsx(Typography, { className: cx(classes.label, classes.scoreLabel), children: "Your Score" }), jsxs(Box, { className: cx(classes.scoreValueContainer), children: [jsxs(Typography, { className: classes.scoreValue, variant: "h1", children: [accuracy, "%"] }), jsx(Typography, { className: classes.label, children: "accuracy" })] }), jsxs(Box, { className: cx(classes.scoreValueContainer), children: [jsxs(Typography, { className: classes.scoreValue, variant: "h1", children: [markedAnswers?.correct, "/", totalQuestions] }), jsx(Typography, { className: classes.label, children: "correct answers" })] })] })] }));
};

const useStyles$d = makeStyles()((theme, { showHint }) => ({
    questionHint: {
        display: "flex",
        gap: "5px"
    },
    label: {
        cursor: "pointer",
        color: theme.palette.secondary.main,
        fontWeight: 600
    },
    bodyContainer: {
        overflow: "hidden",
        transition: "all .5s ease-in",
        opacity: showHint ? 1 : 0,
        maxWidth: showHint ? "350px" : "0px",
        ...(!showHint && { display: "none" })
    },
    body: {
        color: theme.palette.primary.main,
        width: "350px"
    }
}));
const QuestionHint = ({ className, question, ...props }) => {
    const [showHint, setShowHint] = useState(false);
    const { classes, cx } = useStyles$d({ showHint });
    const { icon } = useContext(IconContext);
    if (!question.hint)
        return jsx(Fragment$1, {});
    return (jsxs(Box, { className: cx(classes.questionHint, className), ...props, children: [icon("infoIcon", {
                className: classes.label
            }), jsx(Typography, { "data-test-id": "question-hint-show-hint-btn", className: classes.label, onClick: () => setShowHint(prev => !prev), children: showHint ? "Hide hint:" : "Show hint" }), jsx(Box, { className: classes.bodyContainer, children: jsx(Typography, { className: classes.body, children: question.hint }) })] }));
};

const useStyles$c = makeStyles()((_theme) => ({
    wysiwyg: {
        "& img": {
            maxWidth: "100%"
        }
    }
}));
const WYSIWYG = ({ className, ...props }) => {
    const { classes, cx } = useStyles$c();
    const { getUrl } = useFileBaseUrl();
    return (jsx(ReactMarkdown, { className: cx(classes.wysiwyg, className), rehypePlugins: [rehypeRaw], components: {
            u: (uProps) => jsx("u", { style: { textDecoration: "underline" }, ...uProps }),
            a: (aProps) => jsx("a", { ...aProps, href: (!aProps.href || aProps?.href.includes("http")) ? aProps.href : ("https://" + aProps.href) }),
            img: ({ src, ...imgProps }) => jsx("img", { src: getUrl(src ?? ""), ...imgProps })
        }, ...props }));
};

const useStyles$b = makeStyles()((theme) => ({
    questionContent: {
        display: "flex",
        gap: "5px"
    },
    label: {
        cursor: "pointer",
        color: theme.palette.secondary.main,
        fontWeight: 600
    },
    bodyContainer: {
        overflow: "hidden",
        transition: "all .5s ease-in"
    },
    body: {}
}));
const QuestionContent = ({ className, question, ...props }) => {
    const { classes, cx } = useStyles$b();
    if (!question.content)
        return jsx(Fragment$1, {});
    return (jsx(Box, { className: cx(classes.questionContent, className), ...props, children: jsx(Box, { className: classes.bodyContainer, children: jsx(WYSIWYG, { className: classes.body, children: question.content }) }) }));
};

const useStyles$a = makeStyles()((theme) => ({
    booleanChoiceQuestion: {},
    title: {
        margin: "20px 0 30px 0"
    },
    optionContainer: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
        justifyContent: "space-between",
        columnGap: "30px",
        rowGap: "10px",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0, 1fr)"
        }
    },
    errorMsg: {
        margin: "10px 0",
        color: "red",
        height: "10px"
    }
}));
const BooleanChoiceQuestion = ({ className, question, answer, onChange: onChange, onValidate, isError, ...props }) => {
    const { classes, cx } = useStyles$a();
    useEffect(() => {
        onValidate?.(answer !== undefined);
    }, [answer]);
    return (jsxs(Box, { className: cx(classes.booleanChoiceQuestion, className), ...props, children: [jsx(Typography, { className: classes.title, variant: "h3", children: question.title }), question.content && jsx(QuestionContent, { question: question }), jsx(Box, { className: classes.optionContainer, children: [true, false].map((option, i) => (jsx(ChoiceQuestionOption, { "data-test-id": `boolean-choice-question-option-${option ? "true" : "false"}`, i: i, isSelected: option === answer?.option, onClick: () => onChange({ questionId: question.id, questionType: "booleanChoice", option }), children: option ? "True" : "False" }, `quiz-question-option-${i}`))) }), jsx(Typography, { className: classes.errorMsg, children: isError && "Please select an answer" }), jsx(QuestionHint, { question: question })] }));
};

const useStyles$9 = makeStyles()((theme) => ({
    singleChoiceQuestion: {},
    title: {
        margin: "20px 0 30px 0"
    },
    optionContainer: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
        justifyContent: "space-between",
        columnGap: "30px",
        rowGap: "10px",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0, 1fr)"
        }
    },
    errorMsg: {
        margin: "10px 0",
        color: "red",
        height: "10px"
    },
    description: {}
}));
const SingleChoiceQuestion = ({ className, question, answer, onChange: onChange, onValidate, isError, ...props }) => {
    const { classes, cx } = useStyles$9();
    useEffect(() => {
        onValidate?.(answer !== undefined);
    }, [answer]);
    return (jsxs(Box, { className: cx(classes.singleChoiceQuestion, className), ...props, children: [jsx(Typography, { className: classes.title, variant: "h3", children: question.title }), question.content && jsx(QuestionContent, { question: question }), jsx(Box, { className: classes.optionContainer, children: question.options.map((option, i) => (jsx(ChoiceQuestionOption, { "data-test-id": `single-choice-question-option-${replaceAll(option, " ", "")}`, i: i, isSelected: answer?.option === option, onClick: () => onChange({ questionId: question.id, questionType: "singleChoice", option }), children: option }, `quiz-question-option-${i}`))) }), jsx(Typography, { className: classes.errorMsg, children: isError && "Please select an answer" }), jsx(QuestionHint, { question: question })] }));
};

const useStyles$8 = makeStyles()((theme) => ({
    multipleChoiceQuestion: {},
    title: {
        margin: "20px 0 30px 0"
    },
    optionContainer: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
        justifyContent: "space-between",
        columnGap: "30px",
        rowGap: "10px",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0, 1fr)"
        }
    },
    errorMsg: {
        margin: "10px 0",
        color: "red",
        height: "10px"
    }
}));
const MultipleChoiceQuestion = ({ className, question, answer, onChange, onValidate, isError, ...props }) => {
    const { classes, cx } = useStyles$8();
    const handleOnChange = (option) => {
        let options = [...answer?.options ?? []];
        if (!options)
            options = [option];
        else {
            const index = options.indexOf(option);
            if (index > -1) {
                options.splice(index, 1);
            }
            else {
                options.push(option);
            }
        }
        onChange({ questionId: question.id, questionType: "multipleChoice", options });
    };
    useEffect(() => {
        if (answer) {
            onValidate?.(answer.options.length == question.answerCount);
        }
    }, [answer, question.answerCount]);
    return (jsxs(Box, { className: cx(classes.multipleChoiceQuestion, className), ...props, children: [jsx(Typography, { className: classes.title, variant: "h3", children: question.title }), question.content && jsx(QuestionContent, { question: question }), jsx(Box, { className: classes.optionContainer, children: question.options.map((option, i) => {
                    const selected = answer?.options?.includes(option) ?? false;
                    return (jsx(ChoiceQuestionOption, { "data-test-id": `multiple-choice-question-option-${replaceAll(option, " ", "")}`, i: i, isSelected: selected, onClick: () => handleOnChange(option), isError: selected && isError, children: option }, `quiz-question-option-${i}`));
                }) }), jsx(Typography, { className: classes.errorMsg, children: isError && `Please select ${question.answerCount} answers` }), jsx(QuestionHint, { question: question })] }));
};

const useStyles$7 = makeStyles()((_theme, { hasError, valid }) => ({
    inputText: {},
    input: {
        border: valid ? "1px solid #d1d1d1" : "1px solid red",
        padding: "10px 20px",
        marginTop: "8px",
        width: "100%",
        boxSizing: "border-box"
    },
    label: {},
    errorMessage: {
        maxHeight: hasError ? 50 : 0,
        color: "#f30000",
        fontSize: "16px",
        boxSizing: "border-box",
        transition: "max-height 0.15s ease-out",
        "& p": {
            paddingTop: "5px"
        }
    }
}));
const InputText = ({ className, label, value, containerProps, error, onValidate, ...props }) => {
    const [showPassword, setShowPassword] = useState(false);
    const valid = error === undefined;
    const { classes, cx } = useStyles$7({ hasError: !!error, valid });
    const { visibilityIcon, visibilityOffIcon } = useContext(IconContext);
    useEffect(() => {
        if (error !== undefined) {
            onValidate?.();
        }
    }, [value]);
    const inputProps = {
        disableUnderline: true,
        autoCapitalize: "none",
        autoComplete: "off"
    };
    if (props.type === "password") {
        inputProps["endAdornment"] = (jsx(InputAdornment, { position: "end", children: jsx(IconButton, { "data-test-id": "input-text-show-password-button", onClick: () => setShowPassword((prev) => !prev), edge: "end", children: showPassword ? visibilityIcon : visibilityOffIcon }) }));
    }
    return (jsxs(Box, { className: cx(classes.inputText, className), ...containerProps, children: [label && jsx(Typography, { className: classes.label, children: label }), jsx(TextField, { className: classes.input, id: "input-text", variant: "standard", autoComplete: 'off', InputProps: { ...inputProps, ...props.InputProps }, value: value, inputProps: {
                    "data-test-id": "text-input-" + (label ? replaceAll(label?.toLocaleLowerCase(), " ", "-") : ""),
                    autoCapitalize: "none",
                    autoCorrect: "off"
                }, onBlur: e => {
                    props?.onBlur?.(e);
                    onValidate?.();
                }, ...props, type: props.type === "password" && showPassword ? "text" : props.type }), jsx("div", { className: classes.errorMessage, children: jsx("p", { children: error ?? " " }) })] }));
};

const useStyles$6 = makeStyles()((_theme) => ({
    fillTheBlankQuestion: {},
    title: {
        margin: "20px 0 30px 0"
    },
    optionContainer: {},
    errorMsg: {
        margin: "10px 0",
        color: "red",
        height: "10px"
    },
    inputText: {
        display: "inline-block",
        maxWidth: "20%",
        verticalAlign: "middle"
    },
    fillTheBlankQuestionText: {
        display: "inline-block",
        verticalAlign: "middle",
        marginBottom: "10px"
    }
}));
const FillTheBlankQuestion = ({ className, question, answer, onChange: onChange, onValidate, isError, ...props }) => {
    const { classes, cx } = useStyles$6();
    const [answerLocal, setAnswerLocal] = useState(answer?.options ?? Array(question.answerCount).fill(""));
    const answerCount = answerLocal.filter(e => e.length > 0).length;
    const splitQuestionText = question.title.split("_");
    const handleInputChange = useCallback((e, i) => {
        setAnswerLocal(prev => {
            if (prev[i] === e.target.value) {
                return prev;
            }
            prev[i] = e.target.value;
            return [...prev];
        });
    }, []);
    useEffect(() => {
        onChange({
            ...answer,
            options: answerLocal,
            questionId: question.id,
            questionType: "fillTheBlank"
        });
        onValidate?.(answerCount === question.answerCount);
    }, [onChange, onValidate, answerLocal, question]);
    return (jsxs(Box, { className: cx(classes.fillTheBlankQuestion, className), ...props, children: [jsx(Typography, { className: classes.title, variant: "h3", children: "Fill in the blanks..." }), question.content && jsx(QuestionContent, { question: question }), jsx(Box, { className: classes.optionContainer, children: splitQuestionText.map((questionText, i) => {
                    return jsxs(Fragment, { children: [questionText.split(" ").map((e, j) => jsxs(Typography, { className: classes.fillTheBlankQuestionText, children: [e, "\u00A0"] }, "question-text-word" + j)), i < (splitQuestionText.length - 1) &&
                                jsx(InputText, { "data-test-id": `fill-in-the-blank-question-${question.id}-input`, className: classes.inputText, value: answerLocal[i], onChange: (e) => handleInputChange(e, i) })] }, "question-text-" + i);
                }) }), jsx(Typography, { className: classes.errorMsg, children: isError && `Please enter the remaining ${question.answerCount - answerCount} words` }), jsx(QuestionHint, { question: question })] }));
};

const useStyles$5 = makeStyles()(() => ({
    dragToOrderQuestion: {},
    title: {
        margin: "20px 0 30px 0"
    },
    errorMsg: {
        margin: "10px 0",
        color: "red",
        height: "10px"
    }
}));
const DragToOrderQuestion = ({ className, question, answer, onChange, onValidate, isError, ...props }) => {
    const { classes, cx } = useStyles$5();
    useEffect(() => {
        onValidate?.(answer !== undefined);
    }, [answer]);
    const options = useMemo(() => {
        return question.options.map(option => ({ value: option }));
    }, [question]);
    useEffect(() => {
        onChange({ options: question.options, questionId: question.id, questionType: "dragToOrder" });
    }, [question]);
    return (jsxs(Fragment$1, { children: [jsxs(Box, { className: cx(classes.dragToOrderQuestion, className), ...props, children: [jsx(Typography, { className: classes.title, variant: "h3", children: question.title }), question.content && jsx(QuestionContent, { question: question }), jsx(DraggableList, { list: options, isDragDisabled: false, onChange: (options) => onChange({ options, questionId: question.id, questionType: "dragToOrder" }) })] }), jsx(Typography, { className: classes.errorMsg, children: isError && "Please select an answer" }), jsx(QuestionHint, { question: question })] }));
};

const useStyles$4 = makeStyles()((theme) => ({
    matchingOrderQuestion: {
        display: "flex",
        justifyContent: "space-between",
        gap: "50px",
        [theme.breakpoints.down("sm")]: {
            gap: "10px"
        }
    },
    title: {
        margin: "20px 0 30px 0"
    },
    errorMsg: {
        margin: "10px 0",
        color: "red",
        height: "10px"
    }
}));
const MatchingOrderQuestion = ({ className, question, answer, onChange, onValidate, isError, ...props }) => {
    const { classes, cx } = useStyles$4();
    const container = useRef(null);
    const [minHeight, setMinHeight] = useState(0);
    const [firstRender, setFirstRender] = useState(true);
    useEffect(() => setFirstRender(false), []);
    useEffect(() => {
        onValidate?.(answer !== undefined);
    }, [answer]);
    useEffect(() => {
        onChange({ option: question.options, questionId: question.id, questionType: "matchingOrder" });
    }, [question]);
    useEffect(() => {
        if (firstRender || !container.current)
            return;
        let biggestHeight = 0;
        Array.from(container.current.children).forEach(e => {
            Array.from(e.children).forEach(x => {
                const height = x.offsetHeight;
                if (height > biggestHeight)
                    biggestHeight = height;
            });
        });
        setMinHeight(biggestHeight);
    }, [firstRender]);
    const answers = useMemo(() => {
        return question.answers.map(option => ({ value: option }));
    }, [question]);
    const questionsList = useMemo(() => {
        return question.options.map(option => ({ value: option }));
    }, [question]);
    return (jsxs(Box, { ...props, children: [jsx(Typography, { className: classes.title, variant: "h3", children: question.title }), question.content && jsx(QuestionContent, { question: question }), jsxs(Box, { className: cx(classes.matchingOrderQuestion, className), ref: container, children: [jsx(DraggableList, { list: questionsList, isDragDisabled: true, minHeight: minHeight }), jsx(DraggableList, { list: answers, isDragDisabled: false, onChange: (option) => onChange({ option, questionId: question.id, questionType: "matchingOrder" }), minHeight: minHeight })] }), jsx(Typography, { className: classes.errorMsg, children: isError && "Please select an answer" }), jsx(QuestionHint, { question: question })] }));
};

const useStyles$3 = makeStyles()((theme) => ({
    textQuestion: {},
    title: {
        margin: "20px 0 30px 0"
    },
    inputContainer: {
        boxSizing: "border-box",
        width: "100%",
        backgroundColor: "#fff",
        padding: "10px 20px",
        borderRadius: "10px",
        border: "1px solid #d1d1d1",
        "& textarea": {
            textAlign: "left",
            minHeight: "inherit"
        }
    },
    input: {
        width: "100%",
        minHeight: "100px"
    },
    characterLimitIndicator: {
        color: theme.palette.secondary.main,
        textAlign: "right",
        marginTop: "20px"
    },
    errorMsg: {
        margin: "10px 0",
        color: "red",
        height: "10px"
    }
}));
const TextQuestion = ({ className, question, answer, onChange: onChange, onValidate, isError, ...props }) => {
    const { classes, cx } = useStyles$3();
    useEffect(() => {
        onValidate?.(answer !== undefined);
    }, [answer]);
    const handleChange = (value) => {
        onChange({ questionId: question.id, questionType: question.type, answer: value.slice(0, question.characterLimit) });
    };
    return (jsxs(Box, { className: cx(classes.textQuestion, className), ...props, children: [jsx(Typography, { className: classes.title, variant: "h3", children: question.title }), question.content && jsx(QuestionContent, { question: question }), jsx(TextField, { id: "quiz-text-question-input", multiline: true, className: classes.inputContainer, value: answer?.answer ?? "", placeholder: "...", InputProps: { disableUnderline: true, className: classes.input }, variant: "standard", onChange: e => handleChange(e.target.value) }), jsxs(Typography, { className: classes.characterLimitIndicator, children: [answer?.answer?.length ?? 0, "/", question.characterLimit] }), jsx(Typography, { className: classes.errorMsg, children: isError && "Please fill in your answer" })] }));
};

const useStyles$2 = makeStyles()((theme) => ({
    outlineButton: {
        border: `${theme.palette.secondary.main} 1px solid`,
        color: theme.palette.secondary.main,
        backgroundColor: "transparent",
        "&:hover": {
            backgroundColor: theme.palette.secondary.main + "10"
        }
    }
}));
const OutlineButton = ({ children, className, ...props }) => {
    const { classes, cx } = useStyles$2();
    return (jsx(Button, { className: cx(classes.outlineButton, className), ...props, children: children }));
};

const useStyles$1 = makeStyles()((theme, { isValid, activeQuestionPos, questionCount }) => ({
    quizQuestionContainer: {
        textAlign: "left"
    },
    questionCounter: {
        color: "grey",
        display: "inline-block",
        marginRight: "20px"
    },
    answerCounter: {
        color: "white",
        backgroundColor: theme.palette.secondary.main,
        borderRadius: 100,
        padding: "5px 20px",
        fontSize: "12px",
        display: "inline-block",
        [theme.breakpoints.down("sm")]: {
            float: "right"
        }
    },
    buttonContainer: {
        justifyContent: "space-between",
        display: "flex",
        marginTop: "60px",
        [theme.breakpoints.down("sm")]: {
            marginTop: "40px",
            flexDirection: "column"
        },
        "& > button": {
            padding: "15px 40px",
            opacity: isValid ? 1 : 0.7,
            [theme.breakpoints.down("sm")]: {
                width: "100%",
                marginBottom: "20px"
            }
        }
    },
    prevBtn: {
        cursor: "pointer",
        "&:after": {
            content: activeQuestionPos === 0 ? "\"Back\"" : "\"Previous question\""
        },
        "& svg": {
            transform: "scaleX(-1)",
            marginRight: "5px"
        }
    },
    nextBtn: {
        cursor: isValid ? "pointer" : "not-allowed",
        "&:before": {
            content: questionCount && activeQuestionPos === (questionCount - 1) ? "\"Submit answers\"" : "\"Next question\""
        },
        "& svg": {
            marginLeft: "5px"
        }
    }
}));
const QuizQuestionContainer = ({ activeQuestionPos, questions, onSubmit, onPreviousQuestionAction, onNextQuestionAction, className, ...props }) => {
    const mq = useMq();
    const [answers, setAnswers] = useState([]);
    const activeAnswer = answers.length >= activeQuestionPos ? answers[activeQuestionPos] : undefined;
    const activeQuestion = questions && (questions?.length ?? 0) >= activeQuestionPos ? questions[activeQuestionPos] : undefined;
    const [isValid, setIsValid] = useState(false);
    const [isError, setIsError] = useState(false);
    const { classes, cx } = useStyles$1({ isValid, activeQuestionPos, questionCount: questions?.length });
    const { navigateNextIcon } = useContext(IconContext);
    const handleChange = (answer) => {
        if (!questions)
            return;
        setAnswers(prev => {
            if (prev.length < activeQuestionPos) {
                prev.push(answer);
            }
            else {
                prev[activeQuestionPos] = answer;
            }
            return [...prev];
        });
    };
    const handleNext = () => {
        if (!isValid)
            setIsError(true);
        else {
            onNextQuestionAction();
            setIsValid(false);
        }
    };
    const handlePrevious = () => onPreviousQuestionAction();
    useEffect(() => {
        if (questions && activeQuestionPos >= questions.length) {
            onSubmit?.(answers);
            return;
        }
        setIsError(false);
    }, [activeQuestionPos, questions]);
    useEffect(() => {
        if (isValid)
            setIsError(false);
    }, [isValid]);
    if (questions && activeQuestionPos >= questions.length) {
        return jsx(LinearProgress, {});
    }
    const prevBtn = (jsx(OutlineButton, { "data-test-id": "quiz-question-container-prev-btn", className: classes.prevBtn, onClick: () => handlePrevious(), children: navigateNextIcon }));
    const nextBtn = (jsx(OutlineButton, { "data-test-id": "quiz-question-container-next-btn", className: classes.nextBtn, onClick: () => handleNext(), children: navigateNextIcon }));
    return (jsxs(Box, { className: cx(classes.quizQuestionContainer, className), ...props, children: [jsxs(Box, { className: classes.questionCounter, children: ["Question ", activeQuestionPos + 1, " of ", questions?.length ?? "..."] }), activeQuestion && !isTextQuestion(activeQuestion) &&
                jsxs(Box, { className: classes.answerCounter, children: [activeQuestion && isSingleChoiceQuestion(activeQuestion) && "1 answer", activeQuestion && isBooleanChoiceQuestion(activeQuestion) && "1 answer", activeQuestion && isMultipleChoiceQuestion(activeQuestion) && `${activeQuestion.answerCount} answers`, activeQuestion && isFillTheBlankQuestion(activeQuestion) && `${activeQuestion.answerCount} answers`, activeQuestion && isDragToOrderQuestion(activeQuestion) && "Drag rows into the correct order", activeQuestion && isMatchingOrderQuestion(activeQuestion) && "Drag rows into the correct order"] }), activeQuestion && jsxs(Fragment$1, { children: [isSingleChoiceQuestion(activeQuestion) && (!activeAnswer || isSingleChoiceAnswer(activeAnswer))
                        && jsx(SingleChoiceQuestion, { onValidate: v => setIsValid(v), isError: isError, question: activeQuestion, onChange: handleChange, answer: activeAnswer }), isMultipleChoiceQuestion(activeQuestion) && (!activeAnswer || isMultipleChoiceAnswer(activeAnswer))
                        && jsx(MultipleChoiceQuestion, { onValidate: v => setIsValid(v), isError: isError, question: activeQuestion, onChange: handleChange, answer: activeAnswer }), isBooleanChoiceQuestion(activeQuestion) && (!activeAnswer || isBooleanChoiceAnswer(activeAnswer))
                        && jsx(BooleanChoiceQuestion, { onValidate: v => setIsValid(v), isError: isError, question: activeQuestion, onChange: handleChange, answer: activeAnswer }), isFillTheBlankQuestion(activeQuestion) && (!activeAnswer || isFillTheBlankAnswer(activeAnswer))
                        && jsx(FillTheBlankQuestion, { onValidate: v => setIsValid(v), isError: isError, question: activeQuestion, onChange: handleChange, answer: activeAnswer }), isDragToOrderQuestion(activeQuestion) && (!activeAnswer || isDragToOrderAnswer(activeAnswer))
                        && jsx(DragToOrderQuestion, { onValidate: v => setIsValid(v), isError: isError, question: activeQuestion, onChange: handleChange, answer: activeAnswer }), isMatchingOrderQuestion(activeQuestion) && (!activeAnswer || isMatchingOrderAnswer(activeAnswer))
                        && jsx(MatchingOrderQuestion, { onValidate: v => setIsValid(v), isError: isError, question: activeQuestion, onChange: handleChange, answer: activeAnswer }), isTextQuestion(activeQuestion) && (!activeAnswer || isTextAnswer(activeAnswer))
                        && jsx(TextQuestion, { onValidate: v => setIsValid(v), isError: isError, question: activeQuestion, onChange: handleChange, answer: activeAnswer })] }), jsxs(Box, { className: classes.buttonContainer, children: [!mq.mobile && prevBtn, nextBtn, mq.mobile && prevBtn] })] }));
};

const QuizQuestion = ({ question, answer, ...props }) => {
    if (isSingleChoiceQuestion$1(question) && (!answer || isSingleChoiceAnswer$1(answer)))
        return jsx(SingleChoiceQuestion, { question: question, answer: answer, ...props });
    if (isMultipleChoiceQuestion$1(question) && (!answer || isMultipleChoiceAnswer$1(answer)))
        return jsx(MultipleChoiceQuestion, { question: question, answer: answer, ...props });
    if (isBooleanChoiceQuestion$1(question) && (!answer || isBooleanChoiceAnswer$1(answer)))
        return jsx(BooleanChoiceQuestion, { question: question, answer: answer, ...props });
    if (isFillTheBlankQuestion$1(question) && (!answer || isFillTheBlankAnswer$1(answer)))
        return jsx(FillTheBlankQuestion, { question: question, answer: answer, ...props });
    if (isDragToOrderQuestion$1(question) && (!answer || isDragToOrderAnswer$1(answer)))
        return jsx(DragToOrderQuestion, { question: question, answer: answer, ...props });
    if (isMatchingOrderQuestion$1(question) && (!answer || isMatchingOrderAnswer$1(answer)))
        return jsx(MatchingOrderQuestion, { question: question, answer: answer, ...props });
    if (isTextQuestion(question) && (!answer || isTextAnswer(answer)))
        return jsx(TextQuestion, { question: question, answer: answer, ...props });
    throw new Error(`Question type (${question.type}) not supported in QuizQuestion component!`);
};

const useStyles = makeStyles()((theme, { isValid }) => ({
    quizQuestionContainer: {
        textAlign: "left"
    },
    questionContainer: {},
    questionHeader: {
        display: "flex",
        alignItems: "center",
        marginTop: "55px",
        [theme.breakpoints.down("sm")]: {
            justifyContent: "space-between"
        }
    },
    questionCounter: {
        color: "grey",
        display: "inline-block",
        marginRight: "20px",
        whiteSpace: "nowrap"
    },
    answerCounter: {
        color: "white",
        backgroundColor: theme.palette.secondary.main,
        borderRadius: 100,
        padding: "5px 20px",
        fontSize: "12px",
        display: "inline-block"
    },
    buttonContainer: {
        width: "100%",
        textAlign: "right"
    },
    submitBtn: {
        marginTop: "20px",
        cursor: isValid ? "pointer" : "not-allowed"
    },
    errorMsg: {
        color: theme.palette.error.main,
        transition: "max-height 0.5s ease-out",
        overflow: "hidden"
    },
    errorContainer: {
        marginTop: "30px",
        height: "50px",
        width: "100%",
        textAlign: "center"
    }
}));
const QuizQuestionListContainer = ({ questions, onSubmit, className, ...props }) => {
    const [answers, setAnswers] = useState(Array(questions?.length).fill(undefined));
    const [isValid, setIsValid] = useState(Array(questions?.length).fill(false));
    const [isError, setIsError] = useState(false);
    const { classes, cx } = useStyles({ isValid: isValid.find(e => e) });
    const firstRender = useRef(true);
    useEffect(() => {
        //differ first render to avoid double update
        if (!questions || firstRender.current)
            return;
        setAnswers(Array(questions?.length).fill(undefined));
        setIsValid(Array(questions?.length).fill(false));
    }, [questions]);
    useEffect(() => {
        firstRender.current = true;
    }, []);
    const isQuizValid = useCallback(() => isValid.filter(e => !e).length === 0, [isValid]);
    const handleChange = (answer, i) => {
        if (!questions)
            return;
        setAnswers(prev => {
            if (JSON.stringify(prev[i]) === JSON.stringify(answer)) {
                return prev;
            }
            prev[i] = { ...answer };
            return [...prev];
        });
    };
    const handleValidateChange = (isValid, i) => {
        setIsValid(prev => {
            if (prev[i] === isValid)
                return prev;
            prev[i] = isValid;
            return [...prev];
        });
    };
    useEffect(() => {
        setIsError(prev => {
            //we only attempt to reset error if already set
            if (prev === false)
                return prev;
            return isQuizValid();
        });
    }, [isQuizValid]);
    const handleSubmit = () => {
        const valid = isQuizValid();
        setIsError(!valid);
        if (!valid) {
            return;
        }
        onSubmit?.(answers);
    };
    return (jsxs(Box, { className: cx(classes.quizQuestionContainer, className), ...props, children: [questions?.map((question, i) => jsxs(Box, { className: classes.questionContainer, "data-test-id": `quiz-list-question-${question.id}`, children: [jsxs(Box, { className: classes.questionHeader, children: [jsxs(Box, { className: classes.questionCounter, children: ["Question ", i + 1, " of ", questions?.length ?? "..."] }), question && !isTextQuestion(question) &&
                                jsxs(Box, { className: classes.answerCounter, children: [question && isSingleChoiceQuestion$1(question) && "1 answer", question && isBooleanChoiceQuestion$1(question) && "1 answer", question && isMultipleChoiceQuestion$1(question) && `${question.answerCount} answers`, question && isFillTheBlankQuestion$1(question) && `${question.answerCount} answers`, question && isDragToOrderQuestion$1(question) && "Drag rows into the correct order", question && isMatchingOrderQuestion$1(question) && "Drag rows into the correct order"] })] }), jsx(QuizQuestion, { onValidate: v => handleValidateChange(v, i), isError: isError && !isValid[i], onChange: e => handleChange(e, i), answer: answers[i], question: question })] }, `question_${i}`)), jsx(Box, { className: classes.errorContainer, children: jsxs(Typography, { className: classes.errorMsg, sx: { maxHeight: isError ? "50px" : 0 }, children: [isValid.filter(e => !e).length, " questions require attention before you can submit!"] }) }), jsx(Box, { className: classes.buttonContainer, children: jsx(Button, { "data-test-id": "quiz-question-list-container-submit-btn", className: classes.submitBtn, onClick: handleSubmit, children: "Submit answers" }) })] }));
};

export { BooleanChoiceQuestion, ChoiceQuestionOption, DragToOrderQuestion, DraggableList, FillTheBlankQuestion, MarkedBooleanChoiceQuestion, MarkedDragToOrderQuestion, MarkedFillTheBlankQuestion, MarkedMultipleChoiceQuestion, MarkedQuestion, MarkedQuestionsSummary, MarkedSingleChoiceQuestion, MarkedTextQuestion, MatchingOrderQuestion, MultipleChoiceQuestion, QuestionHint, QuizQuestionContainer, QuizQuestionListContainer, SingleChoiceQuestion, TextQuestion };
